import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useEffect, useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
} from "@volca/ui/components/ui/input-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import type { PaneSearch } from "./use-pane-search";

const ICON = "text-subtle";

/**
 * Find in this pane, drawn like the spreadsheet's own find bar.
 *
 * Collapsed it is one icon at the end of the control row; opened it becomes a
 *
 * Enter and Shift+Enter walk the matches, Escape closes. Those are the keys
 * the grid's search uses and the keys every other find bar uses.
 */
export const PaneSearchControl = ({
  search,
  label,
  className,
}: {
  search: PaneSearch;
  /** Names the field, e.g. "roster". */
  label: string;
  className?: string;
}) => {
  const input = useRef<HTMLInputElement>(null);
  // ⌘F only belongs to a pane that has this bar on screen.
  const { attach } = search;
  useEffect(() => attach(), [attach]);
  useEffect(() => {
    if (!search.open) return;
    input.current?.focus();
    input.current?.select();
  }, [search.focusRequest, search.open]);

  if (!search.open)
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Search the ${label}`}
              onClick={search.show}
              className={cn(ICON, className)}
            />
          }
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
        </TooltipTrigger>
        <TooltipContent>{`Search the ${label}`}</TooltipContent>
      </Tooltip>
    );

  const hits = search.matches.length;
  const typed = search.query.trim().length > 0;
  return (
    <InputGroup className={cn("h-7 w-auto shrink-0", className)}>
      <InputGroupAddon>
        <MagnifyingGlassIcon aria-hidden className="h-4 w-4" />
      </InputGroupAddon>
      <InputGroupInput
        ref={input}
        type="text"
        value={search.query}
        onChange={(event) => search.setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            search.hide();
            return;
          }
          if (event.key === "Enter" && hits) {
            event.preventDefault();
            search.goTo(event.shiftKey ? -1 : 1);
          }
        }}
        aria-label={`Search the ${label}`}
        placeholder={`Find in ${label}`}
        className="h-7 w-44 flex-none px-1 text-xs md:text-xs"
      />
      <InputGroupAddon align="inline-end" className="gap-1">
        {typed && (
          <InputGroupText
            className={cn("text-xs whitespace-nowrap", {
              "text-subtle": hits > 0,
              "text-danger": hits === 0,
            })}
          >
            {hits ? `${search.at + 1} of ${hits}` : "No matches"}
          </InputGroupText>
        )}
        <InputGroupButton
          size="icon-xs"
          aria-label="Previous match"
          disabled={!hits}
          onClick={() => search.goTo(-1)}
          className={ICON}
        >
          <CaretUpIcon className="h-4 w-4" />
        </InputGroupButton>
        <InputGroupButton
          size="icon-xs"
          aria-label="Next match"
          disabled={!hits}
          onClick={() => search.goTo(1)}
          className={ICON}
        >
          <CaretDownIcon className="h-4 w-4" />
        </InputGroupButton>
        <InputGroupButton
          size="icon-xs"
          aria-label="Close search"
          onClick={search.hide}
          className={ICON}
        >
          <XIcon className="h-4 w-4" />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
};
